'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';

import { cn } from '@kit/ui/utils';

import {
  ANIMATION_CONFIG,
  FMARK,
  getThemeColors,
  HEADER_SIZES,
  LOCKUP,
} from './constants';
import type { FlowLogoHeaderProps } from './types';

/**
 * FlowLogoHeader - Navbar/Header variant
 * F-mark icon + "flow" wordmark lockup
 * Static by default with optional hover animation on bars
 *
 * Proportions from logo-design skill:
 *   gap       = icon_height × 0.4
 *   font_size = icon_height × 0.8
 *   baseline  = icon_center + font_size × 0.36
 */
export function FlowLogoHeader({
  className,
  size = 'md',
  theme = 'light',
  color,
  href,
  showText = true,
  animateOnHover = true,
  'aria-label': ariaLabel = 'Flow',
}: FlowLogoHeaderProps) {
  const config = HEADER_SIZES[size];
  const colors = getThemeColors(theme);
  const fillColor = color || colors.primary;

  // Scale lockup to fit the requested height
  const scale = config.height / LOCKUP.viewBox.h;

  // Bar y positions (with padding)
  const p = LOCKUP.padding;
  const bars = [
    { y: p, w: FMARK.widths[0] },
    { y: p + FMARK.barH + FMARK.barGap, w: FMARK.widths[1] },
    { y: p + (FMARK.barH + FMARK.barGap) * 2, w: FMARK.widths[2] },
  ];

  // Text position: cap-height centered with icon
  const iconCenter = p + FMARK.h / 2; // 8 + 30 = 38
  const textX = p + FMARK.w + LOCKUP.gap; // 8 + 52 + 24 = 84
  const textY = iconCenter + LOCKUP.fontSize * 0.36; // 38 + 17.28 ≈ 55

  const viewW = showText ? LOCKUP.viewBox.w : FMARK.w + p * 2;
  const viewH = LOCKUP.viewBox.h;

  const svg = (
    <motion.svg
      viewBox={`0 0 ${viewW} ${viewH}`}
      width={viewW * scale}
      height={viewH * scale}
      className={cn('inline-block', className)}
      fill={fillColor}
      role="img"
      aria-label={ariaLabel}
      initial={false}
      whileHover={animateOnHover ? 'hover' : undefined}
    >
      {/* F-mark bars with hover slide */}
      {bars.map((bar, i) => (
        <motion.rect
          key={i}
          x={p}
          y={bar.y}
          width={bar.w}
          height={FMARK.barH}
          rx={FMARK.rx}
          variants={{
            hover: {
              x: p + (i === 0 ? 2 : i === 1 ? 4 : 6),
              transition: {
                duration: ANIMATION_CONFIG.durations.hover,
                ease: ANIMATION_CONFIG.easing,
              },
            },
          }}
        />
      ))}

      {/* Wordmark */}
      {showText && (
        <text
          x={textX}
          y={textY}
          fontFamily={LOCKUP.fontFamily}
          fontSize={LOCKUP.fontSize}
          fontWeight={LOCKUP.fontWeight}
          letterSpacing={LOCKUP.letterSpacing}
        >
          flow
        </text>
      )}
    </motion.svg>
  );

  if (href != null) {
    return (
      <Link href={href} aria-label={ariaLabel} prefetch={true}>
        {svg}
      </Link>
    );
  }

  return svg;
}
